import { h, Component } from 'preact';
import util from 'preact-util';
import { observer, createRef } from 'mobx-preact';
import { Text, Localizer } from 'preact-i18n';
import Markdown from 'preact-markdown';
import { route } from 'preact-router';

import Hls from 'hls.js';

import localUtil from '../../lib/util';

const SUBTITLE_LANGUAGE_KEY = 'videoSubtitleLanguage';

@observer
class VideoStreamingPlayer extends Component {
  	constructor(props) {
        super(props);
        this.state = {
            currentTime: 0,
            showTranscript: false,
            subtitleLanguage: null, // null = off, 'original', 'en', 'es', 'fr', 'fi'
            dvrStartTime: 0,
            dvrEndTime: 0,
            isAtLiveEdge: true,
            isDvrAvailable: false,
            isDraggingScrubber: false,
            dragTime: null,
            isPlaying: false,
            volume: 1,
            isMuted: false,
            showVolumeSlider: false,
            hoverTime: null,
            hoverPercent: null,
            isScrubberHovered: false,
            bufferedRanges: [],
            vttCues: null,
            vttBaseUrl: '',
            isFullscreen: false,
            isCssFullscreen: false,
        };
        this.playerRef = {};
        this.hls = {};
        this.heartbeatInterval = null;
        this.watchMinutes = 0;
        this.scrubberRef = null;
        this.containerRef = null;
        this.volumeRef = null;
        this.seekThrottleTimer = null;
        this.vttAbortController = null;
        this.spriteImages = {};
        this.volumeHideTimer = null;
        this.savedBodyOverflow = null;
    }

    setPlayerRef = (playerRef) => {
        const { videoStreamUrl } = this.props;
        this.playerRef[videoStreamUrl] = playerRef;

        // Add time update listener for transcript synchronization
        if (playerRef) {
            playerRef.addEventListener('timeupdate', this.handleTimeUpdate);
            playerRef.addEventListener('play', this.handlePlay);
            playerRef.addEventListener('pause', this.handlePause);
        }

        this.loadHls(playerRef);
    }

    handleTimeUpdate = (e) => {
        const video = e.target;
        const currentTime = video.currentTime;
        const newState = { currentTime };

        if (video.seekable && video.seekable.length > 0) {
            const dvrStartTime = video.seekable.start(0);
            const dvrEndTime = video.seekable.end(0);
            const dvrWindow = dvrEndTime - dvrStartTime;
            newState.dvrStartTime = dvrStartTime;
            newState.dvrEndTime = dvrEndTime;
            newState.isDvrAvailable = dvrWindow > 10;
            newState.isAtLiveEdge = (dvrEndTime - currentTime) < 30;

            // Capture buffered ranges for progress bar
            if (video.buffered && video.buffered.length > 0 && dvrWindow > 0) {
                const ranges = [];
                for (let i = 0; i < video.buffered.length; i++) {
                    const startPct = ((video.buffered.start(i) - dvrStartTime) / dvrWindow) * 100;
                    const endPct = ((video.buffered.end(i) - dvrStartTime) / dvrWindow) * 100;
                    ranges.push({
                        start: Math.max(0, Math.min(100, startPct)),
                        end: Math.max(0, Math.min(100, endPct)),
                    });
                }
                newState.bufferedRanges = ranges;
            }
        }

        this.setState(newState);
    }

    toggleTranscript = () => {
        const { transcriptEnglish, transcriptSpanish, transcriptFrench, transcriptFinnish } = this.props;
        const { subtitleLanguage } = this.state;

        // Build list of available languages
        const availableLanguages = ['original'];
        if (transcriptEnglish) availableLanguages.push('en');
        if (transcriptSpanish) availableLanguages.push('es');
        if (transcriptFrench) availableLanguages.push('fr');
        if (transcriptFinnish) availableLanguages.push('fi');

        let newLanguage = null;
        let newShowTranscript = false;

        // Cycle through available languages: off -> original -> en -> es -> fr -> fi -> off
        if (subtitleLanguage === null) {
            newLanguage = availableLanguages[0]; // Start with original
            newShowTranscript = true;
        } else {
            const currentIndex = availableLanguages.indexOf(subtitleLanguage);
            if (currentIndex !== -1 && currentIndex < availableLanguages.length - 1) {
                // Move to next available language
                newLanguage = availableLanguages[currentIndex + 1];
                newShowTranscript = true;
            } else {
                // Turn off (cycle complete)
                newLanguage = null;
                newShowTranscript = false;
            }
        }

        // Save preference to localStorage
        util.set(SUBTITLE_LANGUAGE_KEY, newLanguage);

        this.setState({
            subtitleLanguage: newLanguage,
            showTranscript: newShowTranscript
        });
    }

    startHeartbeat = () => {
        if (this.heartbeatInterval) return;
        const { stores, stream, streamId, videoStreamUrl } = this.props;
        if (!stores || !stores.appState) return;

        this.heartbeatInterval = setInterval(() => {
            this.watchMinutes += 1;
            stores.appState.trackStream('heartbeat', stream || { id: streamId }, {
                watchMinutes: this.watchMinutes,
                url: videoStreamUrl,
            });
        }, 60000);
    }

    stopHeartbeat = () => {
        if (this.heartbeatInterval) {
            clearInterval(this.heartbeatInterval);
            this.heartbeatInterval = null;
        }
    }

    handlePlay = () => {
        this.setState({ isPlaying: true });
        this.startHeartbeat();
    }

    handlePause = () => {
        this.setState({ isPlaying: false });
        this.stopHeartbeat();
    }

    loadHls = (playerRef) => {
        const { hlsIsSupported = false } = this.state;
        const { videoStreamUrl } = this.props;
        if (Hls.isSupported()) {
            this.hls[videoStreamUrl] = new Hls({
                maxMaxBufferLength: 300,
                backBufferLength: 600,
                nudgeMaxRetry: 5,
                maxBufferSize: 600 * 1000 * 1000,
                maxBufferLength: 1200,
                lowLatencyMode: false,
                liveDurationInfinity: true,
                // NO liveSyncDuration — use HLS.js defaults from playlist target duration
                // NO liveMaxLatencyDuration — prevents auto-seeking to live edge
            });
            this.hls[videoStreamUrl].on(Hls.Events.ERROR, (event, data) => {
                if (data.fatal) {
                    switch (data.type) {
                        case Hls.ErrorTypes.NETWORK_ERROR:
                            // Attempt to recover from network errors
                            console.log('Fatal network error encountered, trying to recover.');
                            this.hls[videoStreamUrl].startLoad(); // Try reloading the stream
                            break;
                        case Hls.ErrorTypes.MEDIA_ERROR:
                            console.log('Fatal media error encountered, trying to recover.');
                            this.hls[videoStreamUrl].recoverMediaError(); // Attempt to recover from media errors
                            break;
                        default:
                            console.log('Fatal error encountered, cannot recover.');
                            // If recovery is not possible, consider gracefully destroying the Hls instance
                            // and perhaps signaling to the user that playback cannot continue.
                            this.hls[videoStreamUrl].destroy();
                            break;
                    }
                }
            });
            this.hls[videoStreamUrl].on(Hls.Events.LEVEL_UPDATED, () => {
                const video = this.playerRef[videoStreamUrl];
                if (video && video.seekable && video.seekable.length > 0) {
                    const dvrStartTime = video.seekable.start(0);
                    const dvrEndTime = video.seekable.end(0);
                    const dvrWindow = dvrEndTime - dvrStartTime;
                    this.setState({
                        dvrStartTime,
                        dvrEndTime,
                        isDvrAvailable: dvrWindow > 10,
                        isAtLiveEdge: (dvrEndTime - video.currentTime) < 30,
                    });
                }
            });
            if (playerRef) {
                playerRef.addEventListener('play', () => {
                    console.log('Event "play": Attempting to play video:', videoStreamUrl);

                    // Track video play for usage stats
                    const { stores, id: articleId, stream, streamId } = this.props;
                    if (stores && stores.appState) {
                        if (stream || streamId) {
                            // Stream-specific tracking
                            stores.appState.trackStream('play', stream || { id: streamId }, {
                                url: videoStreamUrl,
                            });
                        } else if (articleId) {
                            // Legacy article tracking
                            stores.appState.addUsageLog({
                                type: 'feed_video_play',
                                props: {
                                    articleId,
                                    url: videoStreamUrl,
                                    source: 'stream',
                                },
                                timestamp: Math.floor(new Date().getTime() / 1000),
                            });
                        }
                    }

                    if (!this.hls[videoStreamUrl].url) {
                        this.hls[videoStreamUrl].loadSource(videoStreamUrl);
                        this.hls[videoStreamUrl].attachMedia(playerRef);
                        this.hls[videoStreamUrl].on(Hls.Events.MANIFEST_PARSED, () => {
                            playerRef.play().catch(e => console.error("Error attempting to play video:", e));
                        });
                    }
                }, { once: true });
            }

        } else if (playerRef && playerRef.canPlayType('application/vnd.apple.mpegurl')) {
            // Native HLS support (iOS Safari)
            playerRef.addEventListener('play', () => {
                console.log('Event "play" (native HLS): Playing video:', videoStreamUrl);

                // Track video play for usage stats
                const { stores, id: articleId, stream, streamId } = this.props;
                if (stores && stores.appState) {
                    if (stream || streamId) {
                        stores.appState.trackStream('play', stream || { id: streamId }, {
                            url: videoStreamUrl,
                        });
                    } else if (articleId) {
                        stores.appState.addUsageLog({
                            type: 'feed_video_play',
                            props: {
                                articleId,
                                url: videoStreamUrl,
                                source: 'stream',
                            },
                            timestamp: Math.floor(new Date().getTime() / 1000),
                        });
                    }
                }
            }, { once: true });
            // src is already set via JSX attribute — native Safari handles HLS directly
        }
    }

    togglePlayPause = () => {
        const { videoStreamUrl } = this.props;
        const video = this.playerRef[videoStreamUrl];
        if (!video) return;
        if (video.paused) {
            video.play().catch(e => console.error('Error playing video:', e));
        } else {
            video.pause();
        }
    }

    toggleVolumeSlider = (e) => {
        if (e) e.stopPropagation();
        this.setState(prev => {
            const willShow = !prev.showVolumeSlider;
            if (willShow) {
                this.startVolumeHideTimer();
            } else {
                this.clearVolumeHideTimer();
            }
            return { showVolumeSlider: willShow };
        });
    }

    startVolumeHideTimer = () => {
        this.clearVolumeHideTimer();
        this.volumeHideTimer = setTimeout(() => {
            this.setState({ showVolumeSlider: false });
        }, 2000);
    }

    clearVolumeHideTimer = () => {
        if (this.volumeHideTimer) {
            clearTimeout(this.volumeHideTimer);
            this.volumeHideTimer = null;
        }
    }

    handleVolumeChange = (e) => {
        const { videoStreamUrl } = this.props;
        const video = this.playerRef[videoStreamUrl];
        if (!video) return;
        const vol = parseFloat(e.target.value);
        video.volume = vol;
        video.muted = vol === 0;
        this.setState({ volume: vol, isMuted: vol === 0 });
        this.startVolumeHideTimer();
    }

    supportsNativeFullscreen = () => {
        const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) ||
            (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
        if (isIOS) return false;
        return !!(document.fullscreenEnabled || document.webkitFullscreenEnabled);
    }

    toggleFullscreen = () => {
        const { isFullscreen } = this.state;
        if (isFullscreen) {
            if (this.supportsNativeFullscreen()) {
                if (document.fullscreenElement) {
                    document.exitFullscreen();
                } else if (document.webkitFullscreenElement) {
                    document.webkitExitFullscreen();
                }
            } else {
                this.exitCssFullscreen();
            }
        } else {
            if (this.supportsNativeFullscreen() && this.containerRef) {
                if (this.containerRef.requestFullscreen) {
                    this.containerRef.requestFullscreen();
                } else if (this.containerRef.webkitRequestFullscreen) {
                    this.containerRef.webkitRequestFullscreen();
                }
            } else {
                this.enterCssFullscreen();
            }
        }
    }

    enterCssFullscreen = () => {
        if (!this.containerRef) return;
        this.savedBodyOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        document.documentElement.classList.add('video-fullscreen-active');
        this.setState({ isFullscreen: true, isCssFullscreen: true });
        document.addEventListener('keydown', this.handleFullscreenEscape);
    }

    exitCssFullscreen = () => {
        document.body.style.overflow = this.savedBodyOverflow || '';
        this.savedBodyOverflow = null;
        document.documentElement.classList.remove('video-fullscreen-active');
        this.setState({ isFullscreen: false, isCssFullscreen: false });
        document.removeEventListener('keydown', this.handleFullscreenEscape);
    }

    handleFullscreenEscape = (e) => {
        if (e.key === 'Escape') {
            this.exitCssFullscreen();
        }
    }

    handleFullscreenChange = () => {
        const isNativeFullscreen = !!(document.fullscreenElement || document.webkitFullscreenElement);
        this.setState({ isFullscreen: isNativeFullscreen });
    }

    handleVideoClick = (e) => {
        localUtil.captureEvents(e);
        this.togglePlayPause();
        if (this.containerRef) {
            this.containerRef.focus();
        }
    }

    // DVR seek methods
    seekToStart = () => {
        const { videoStreamUrl } = this.props;
        const video = this.playerRef[videoStreamUrl];
        if (video && video.seekable && video.seekable.length > 0) {
            video.currentTime = video.seekable.start(0);
        }
    }

    seekToLive = () => {
        const { videoStreamUrl } = this.props;
        const video = this.playerRef[videoStreamUrl];
        const hls = this.hls[videoStreamUrl];
        if (video && hls && hls.liveSyncPosition) {
            video.currentTime = hls.liveSyncPosition;
        } else if (video && video.seekable && video.seekable.length > 0) {
            video.currentTime = video.seekable.end(0);
        }
    }

    seekToPosition = (time) => {
        const { videoStreamUrl } = this.props;
        const video = this.playerRef[videoStreamUrl];
        if (video && video.seekable && video.seekable.length > 0) {
            const start = video.seekable.start(0);
            const end = video.seekable.end(0);
            video.currentTime = Math.max(start, Math.min(end, time));
        }
    }

    // DVR scrubber interaction
    getDvrProgress = () => {
        const { currentTime, dvrStartTime, dvrEndTime, isDraggingScrubber, dragTime } = this.state;
        if (!dvrEndTime || dvrStartTime === dvrEndTime) return 100;
        const time = isDraggingScrubber && dragTime !== null ? dragTime : currentTime;
        const progress = ((time - dvrStartTime) / (dvrEndTime - dvrStartTime)) * 100;
        return Math.max(0, Math.min(100, progress));
    }

    getTimeFromScrubberPosition = (clientX) => {
        if (!this.scrubberRef) return null;
        const { dvrStartTime, dvrEndTime } = this.state;
        const rect = this.scrubberRef.getBoundingClientRect();
        const x = clientX - rect.left;
        const percent = Math.max(0, Math.min(1, x / rect.width));
        return dvrStartTime + (dvrEndTime - dvrStartTime) * percent;
    }

    handleScrubberClick = (e) => {
        if (this.state.isDraggingScrubber) return;
        const newTime = this.getTimeFromScrubberPosition(e.clientX);
        if (newTime !== null) {
            this.seekToPosition(newTime);
        }
    }

    handleScrubberDragStart = (e) => {
        e.preventDefault();
        const newTime = this.getTimeFromScrubberPosition(e.clientX);
        this.setState({ isDraggingScrubber: true, dragTime: newTime });
        document.addEventListener('mousemove', this.handleScrubberDragMove);
        document.addEventListener('mouseup', this.handleScrubberDragEnd);
    }

    handleScrubberDragMove = (e) => {
        if (!this.state.isDraggingScrubber) return;
        const newTime = this.getTimeFromScrubberPosition(e.clientX);
        if (newTime !== null) {
            this.setState({ dragTime: newTime });
            // Throttle actual seeks to every 250ms to avoid overwhelming the player
            if (!this.seekThrottleTimer) {
                this.seekThrottleTimer = setTimeout(() => {
                    this.seekThrottleTimer = null;
                    const { dragTime: latestDragTime } = this.state;
                    if (latestDragTime !== null) {
                        this.seekToPosition(latestDragTime);
                    }
                }, 250);
            }
        }
    }

    handleScrubberDragEnd = () => {
        const { dragTime } = this.state;
        // Clear throttle and do final seek
        if (this.seekThrottleTimer) {
            clearTimeout(this.seekThrottleTimer);
            this.seekThrottleTimer = null;
        }
        if (dragTime !== null) {
            this.seekToPosition(dragTime);
        }
        this.setState({ isDraggingScrubber: false, dragTime: null });
        document.removeEventListener('mousemove', this.handleScrubberDragMove);
        document.removeEventListener('mouseup', this.handleScrubberDragEnd);
        document.removeEventListener('touchmove', this.handleScrubberTouchMove);
        document.removeEventListener('touchend', this.handleScrubberDragEnd);
    }

    handleScrubberTouchStart = (e) => {
        e.preventDefault();
        const touch = e.touches[0];
        const newTime = this.getTimeFromScrubberPosition(touch.clientX);
        this.setState({ isDraggingScrubber: true, dragTime: newTime });
        document.addEventListener('touchmove', this.handleScrubberTouchMove);
        document.addEventListener('touchend', this.handleScrubberDragEnd);
    }

    handleScrubberTouchMove = (e) => {
        if (!this.state.isDraggingScrubber) return;
        const touch = e.touches[0];
        const newTime = this.getTimeFromScrubberPosition(touch.clientX);
        if (newTime !== null) {
            this.setState({ dragTime: newTime });
            if (!this.seekThrottleTimer) {
                this.seekThrottleTimer = setTimeout(() => {
                    this.seekThrottleTimer = null;
                    const { dragTime: latestDragTime } = this.state;
                    if (latestDragTime !== null) {
                        this.seekToPosition(latestDragTime);
                    }
                }, 250);
            }
        }
    }

    formatTime = (seconds) => {
        const totalSec = Math.max(0, Math.round(seconds));
        const h = Math.floor(totalSec / 3600);
        const m = Math.floor((totalSec % 3600) / 60);
        const s = totalSec % 60;
        if (h > 0) {
            return `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
        }
        return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
    }

    handleScrubberMouseMove = (e) => {
        if (!this.scrubberRef) return;
        const { dvrStartTime, dvrEndTime } = this.state;
        const rect = this.scrubberRef.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const percent = Math.max(0, Math.min(1, x / rect.width));
        const hoverTime = dvrStartTime + (dvrEndTime - dvrStartTime) * percent;
        this.setState({
            hoverTime,
            hoverPercent: percent * 100,
            isScrubberHovered: true,
        });
    }

    handleScrubberMouseLeave = () => {
        this.setState({
            hoverTime: null,
            hoverPercent: null,
            isScrubberHovered: false,
        });
    }

    handleKeyDown = (e) => {
        // Don't capture keys when typing in form elements
        const tag = e.target.tagName;
        if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;

        switch (e.key) {
            case 'ArrowLeft':
                e.preventDefault();
                this.seekRelative(-5);
                break;
            case 'ArrowRight':
                e.preventDefault();
                this.seekRelative(5);
                break;
            case 'j':
                e.preventDefault();
                this.seekRelative(-10);
                break;
            case 'l':
                e.preventDefault();
                this.seekRelative(10);
                break;
            case 'k':
            case ' ':
                e.preventDefault();
                this.togglePlayPause();
                break;
            case 'f':
                e.preventDefault();
                this.toggleFullscreen();
                break;
            case 'Escape':
                if (this.state.isFullscreen) {
                    e.preventDefault();
                    this.toggleFullscreen();
                }
                break;
            default:
                break;
        }
    }

    seekRelative = (deltaSeconds) => {
        const { videoStreamUrl } = this.props;
        const video = this.playerRef[videoStreamUrl];
        if (!video || !video.seekable || video.seekable.length === 0) return;
        const start = video.seekable.start(0);
        const end = video.seekable.end(0);
        const newTime = Math.max(start, Math.min(end, video.currentTime + deltaSeconds));
        video.currentTime = newTime;
    }

    formatBehindLive = (seconds) => {
        if (seconds < 30) return 'LIVE';
        const totalSec = Math.round(seconds);
        const h = Math.floor(totalSec / 3600);
        const m = Math.floor((totalSec % 3600) / 60);
        const s = totalSec % 60;
        if (h > 0) {
            return `-${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
        }
        return `-${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
    }

    // --- Thumbnail preview (VTT/sprite) methods ---

    deriveVttUrl = (videoStreamUrl) => {
        if (!videoStreamUrl || !videoStreamUrl.includes('stream.m3u8')) return null;
        return videoStreamUrl.replace('stream.m3u8', 'thumbnails.vtt');
    }

    loadThumbnailVtt = (videoStreamUrl) => {
        // Abort any previous fetch
        if (this.vttAbortController) {
            this.vttAbortController.abort();
        }

        const vttUrl = this.deriveVttUrl(videoStreamUrl);
        if (!vttUrl) return;

        this.vttAbortController = new AbortController();
        const { signal } = this.vttAbortController;

        const baseUrl = vttUrl.substring(0, vttUrl.lastIndexOf('/') + 1);

        fetch(vttUrl, { signal })
            .then(res => {
                if (!res.ok) return null;
                return res.text();
            })
            .then(text => {
                if (!text) return;
                const cues = this.parseVtt(text);
                this.setState({ vttCues: cues, vttBaseUrl: baseUrl });
            })
            .catch(err => {
                if (err.name !== 'AbortError') {
                    console.log('Thumbnail VTT not available:', err.message);
                }
            });
    }

    parseVtt = (vttText) => {
        const lines = vttText.trim().split('\n');
        const cues = [];
        let i = 0;
        // Skip WEBVTT header
        while (i < lines.length && !lines[i].includes('-->')) {
            i++;
        }
        while (i < lines.length) {
            const line = lines[i].trim();
            if (line.includes('-->')) {
                const [startStr, endStr] = line.split('-->').map(s => s.trim());
                const startTime = this.parseVttTimestamp(startStr);
                const endTime = this.parseVttTimestamp(endStr);
                i++;
                if (i < lines.length) {
                    const dataLine = lines[i].trim();
                    const hashIdx = dataLine.indexOf('#xywh=');
                    if (hashIdx !== -1) {
                        const sprite = dataLine.substring(0, hashIdx);
                        const coords = dataLine.substring(hashIdx + 6).split(',').map(Number);
                        cues.push({
                            startTime,
                            endTime,
                            sprite,
                            x: coords[0],
                            y: coords[1],
                            w: coords[2],
                            h: coords[3],
                        });
                    }
                }
            }
            i++;
        }
        return cues;
    }

    parseVttTimestamp = (str) => {
        const parts = str.split(':');
        const h = parseInt(parts[0], 10);
        const m = parseInt(parts[1], 10);
        const s = parseFloat(parts[2]);
        return h * 3600 + m * 60 + s;
    }

    preloadSpriteImage = (baseUrl, spriteFile) => {
        const key = spriteFile;
        if (this.spriteImages[key]) return this.spriteImages[key];
        const img = new Image();
        img.src = baseUrl + spriteFile;
        this.spriteImages[key] = img;
        return img;
    }

    getThumbnailForTime = (time) => {
        const { vttCues, vttBaseUrl, dvrStartTime } = this.state;
        if (!vttCues || vttCues.length === 0) return null;

        // For VOD, dvrStartTime is 0; for DVR, offset the hover time
        const seekTime = time - dvrStartTime;
        if (seekTime < 0) return null;

        const cue = vttCues.find(c => seekTime >= c.startTime && seekTime < c.endTime);
        if (!cue) return null;

        // Preload this sprite and the next one for smooth scrubbing
        const img = this.preloadSpriteImage(vttBaseUrl, cue.sprite);

        // Find next sprite file and preload it
        const cueIdx = vttCues.indexOf(cue);
        for (let i = cueIdx + 1; i < vttCues.length && i <= cueIdx + 10; i++) {
            if (vttCues[i].sprite !== cue.sprite) {
                this.preloadSpriteImage(vttBaseUrl, vttCues[i].sprite);
                break;
            }
        }

        return {
            url: img.src,
            x: cue.x,
            y: cue.y,
            w: cue.w,
            h: cue.h,
        };
    }

    cleanupVideoPlayer = () => {
        // Clean up fullscreen state and listeners
        document.removeEventListener('fullscreenchange', this.handleFullscreenChange);
        document.removeEventListener('webkitfullscreenchange', this.handleFullscreenChange);
        document.removeEventListener('keydown', this.handleFullscreenEscape);
        if (this.state.isCssFullscreen) {
            this.exitCssFullscreen();
        }

        this.clearVolumeHideTimer();
        if (this.vttAbortController) {
            this.vttAbortController.abort();
            this.vttAbortController = null;
        }
        if (this.seekThrottleTimer) {
            clearTimeout(this.seekThrottleTimer);
            this.seekThrottleTimer = null;
        }
        const { videoStreamUrl, stores, stream, streamId } = this.props;
        const { hlsIsSupported = false } = this.state;

        // Clean up DVR scrubber drag listeners
        document.removeEventListener('mousemove', this.handleScrubberDragMove);
        document.removeEventListener('mouseup', this.handleScrubberDragEnd);
        document.removeEventListener('touchmove', this.handleScrubberTouchMove);
        document.removeEventListener('touchend', this.handleScrubberDragEnd);

        // Stop heartbeat and send final stop event
        this.stopHeartbeat();
        if (this.watchMinutes > 0 && stores && stores.appState) {
            stores.appState.trackStream('stop', stream || { id: streamId }, {
                watchMinutes: this.watchMinutes,
                url: videoStreamUrl,
            });
        }

        if (this.playerRef[videoStreamUrl] && Hls.isSupported()) {
            console.log('Unmounting video player');
            // Remove event listeners
            this.playerRef[videoStreamUrl].removeEventListener('timeupdate', this.handleTimeUpdate);
            this.playerRef[videoStreamUrl].removeEventListener('play', this.handlePlay);
            this.playerRef[videoStreamUrl].removeEventListener('pause', this.handlePause);
            this.playerRef[videoStreamUrl].pause();
            this.playerRef[videoStreamUrl].src = ''; // Remove the source
            // this.playerRef[videoStreamUrl].removeAttribute('src'); // Ensure the source is fully removed
            this.playerRef[videoStreamUrl].load(); // Load the empty source
            // this.playerRef[videoStreamUrl].play();
            try {
                this.playerRef[videoStreamUrl].remove();
            } catch(e){
                console.log(e);
            }
            this.playerRef[videoStreamUrl] = null;
        }

        // console.log('this.hls[videoStreamUrl]', this.hls[videoStreamUrl]);
        if (this.hls[videoStreamUrl] && Hls.isSupported()) {
            this.hls[videoStreamUrl].stopLoad();
            this.hls[videoStreamUrl].detachMedia();
            this.hls[videoStreamUrl].destroy(); // This will clean up the hls.js instance
            // console.log('this.hls[videoStreamUrl]', this.hls[videoStreamUrl]);
            this.hls[videoStreamUrl] = null;
            // console.log('this.hls[videoStreamUrl]', this.hls[videoStreamUrl]);
        }
    }

    componentDidMount() {
        const hlsIsSupported = Hls.isSupported();

        // Load saved subtitle language preference from localStorage
        const savedSubtitleLanguage = util.get(SUBTITLE_LANGUAGE_KEY);
        const showTranscript = savedSubtitleLanguage !== null;

        this.setState({
            hlsIsSupported,
            subtitleLanguage: savedSubtitleLanguage,
            showTranscript,
        });

        // Listen for native fullscreen changes
        document.addEventListener('fullscreenchange', this.handleFullscreenChange);
        document.addEventListener('webkitfullscreenchange', this.handleFullscreenChange);

        // Load thumbnail VTT for VOD streams
        const { videoStreamUrl } = this.props;
        if (videoStreamUrl) {
            this.loadThumbnailVtt(videoStreamUrl);
        }
    }

    componentDidUpdate(prevProps) {
        const { videoStreamUrl } = this.props;
        if (videoStreamUrl && prevProps.videoStreamUrl && videoStreamUrl !== prevProps.videoStreamUrl) {
            console.log('Stream URL changed, swapping source:', prevProps.videoStreamUrl, '->', videoStreamUrl);
            const wasPlaying = this.state.isPlaying;

            // Reset VTT state and reload for new URL
            this.spriteImages = {};
            this.setState({ vttCues: null, vttBaseUrl: '' });
            this.loadThumbnailVtt(videoStreamUrl);
            // Clean up old HLS instance
            const oldUrl = prevProps.videoStreamUrl;
            if (this.playerRef[oldUrl]) {
                this.playerRef[oldUrl].removeEventListener('timeupdate', this.handleTimeUpdate);
                this.playerRef[oldUrl].removeEventListener('play', this.handlePlay);
                this.playerRef[oldUrl].removeEventListener('pause', this.handlePause);
            }
            if (this.hls[oldUrl]) {
                this.hls[oldUrl].stopLoad();
                this.hls[oldUrl].detachMedia();
                this.hls[oldUrl].destroy();
                this.hls[oldUrl] = null;
            }
            // Reuse the existing video element with the new URL
            const video = this.playerRef[oldUrl];
            if (video) {
                this.playerRef[videoStreamUrl] = video;
                delete this.playerRef[oldUrl];
                video.addEventListener('timeupdate', this.handleTimeUpdate);
                video.addEventListener('play', this.handlePlay);
                video.addEventListener('pause', this.handlePause);
                // Create new HLS instance and load
                this.loadHls(video);
                if (wasPlaying && this.hls[videoStreamUrl]) {
                    this.hls[videoStreamUrl].loadSource(videoStreamUrl);
                    this.hls[videoStreamUrl].attachMedia(video);
                    this.hls[videoStreamUrl].on(Hls.Events.MANIFEST_PARSED, () => {
                        video.play().catch(e => console.error('Error playing after source swap:', e));
                    });
                }
            }
        }
    }

    componentWillUnmount() {
        this.cleanupVideoPlayer();
    }

    render() {
        const {
            videoStreamUrl,
            poster,
            transcript,
            transcriptionSegments,
            transcriptionLanguage,
            transcriptEnglish,
            transcriptionSegmentsEnglish,
            transcriptSpanish,
            transcriptionSegmentsSpanish,
            transcriptFrench,
            transcriptionSegmentsFrench,
            transcriptFinnish,
            transcriptionSegmentsFinnish
        } = this.props;
        const { currentTime, showTranscript, subtitleLanguage, isAtLiveEdge, isDvrAvailable, isDraggingScrubber, dragTime, dvrEndTime, dvrStartTime, isPlaying, volume, isMuted, showVolumeSlider, hoverTime, hoverPercent, isScrubberHovered, bufferedRanges, isFullscreen, isCssFullscreen } = this.state;

        const hasTranscript = transcript && transcript.length > 0;
        const hasEnglish = transcriptEnglish && transcriptEnglish.length > 0;
        const hasSpanish = transcriptSpanish && transcriptSpanish.length > 0;
        const hasFrench = transcriptFrench && transcriptFrench.length > 0;
        const hasFinnish = transcriptFinnish && transcriptFinnish.length > 0;

        // Choose which segments to use based on selected language
        let activeSegments = null;
        if (subtitleLanguage === 'original') {
            activeSegments = transcriptionSegments;
        } else if (subtitleLanguage === 'en') {
            activeSegments = transcriptionSegmentsEnglish;
        } else if (subtitleLanguage === 'es') {
            activeSegments = transcriptionSegmentsSpanish;
        } else if (subtitleLanguage === 'fr') {
            activeSegments = transcriptionSegmentsFrench;
        } else if (subtitleLanguage === 'fi') {
            activeSegments = transcriptionSegmentsFinnish;
        }

        // Find the current subtitle segment
        let currentSubtitle = null;
        if (activeSegments && activeSegments.length > 0 && showTranscript) {
            currentSubtitle = activeSegments.find(segment =>
                currentTime >= segment.start && currentTime < (segment.end || Infinity)
            );
        }

        // Button tooltip text
        let buttonTitle = 'Show subtitles';
        if (subtitleLanguage === 'original') {
            const nextLang = hasEnglish ? 'English' : (hasSpanish ? 'Spanish' : (hasFrench ? 'French' : (hasFinnish ? 'Finnish' : null)));
            buttonTitle = nextLang ? `Switch to ${nextLang}` : 'Hide subtitles';
        } else if (subtitleLanguage === 'en') {
            buttonTitle = hasSpanish ? 'Switch to Spanish' : (hasFrench ? 'Switch to French' : (hasFinnish ? 'Switch to Finnish' : 'Hide subtitles'));
        } else if (subtitleLanguage === 'es') {
            buttonTitle = hasFrench ? 'Switch to French' : (hasFinnish ? 'Switch to Finnish' : 'Hide subtitles');
        } else if (subtitleLanguage === 'fr') {
            buttonTitle = hasFinnish ? 'Switch to Finnish' : 'Hide subtitles';
        } else if (subtitleLanguage === 'fi') {
            buttonTitle = 'Hide subtitles';
        }

        // Button display text/icon
        let buttonContent = <i class='fa-solid fa-closed-captioning' />;
        if (subtitleLanguage === 'original') {
            // Always show 2-letter language code (NO, EN, etc.)
            const langCode = transcriptionLanguage ? transcriptionLanguage.substring(0, 2).toUpperCase() : 'NO';
            buttonContent = <small class='fw-bold'>{langCode}</small>;
        } else if (subtitleLanguage === 'en') {
            buttonContent = <small class='fw-bold'>EN</small>;
        } else if (subtitleLanguage === 'es') {
            buttonContent = <small class='fw-bold'>ES</small>;
        } else if (subtitleLanguage === 'fr') {
            buttonContent = <small class='fw-bold'>FR</small>;
        } else if (subtitleLanguage === 'fi') {
            buttonContent = <small class='fw-bold'>FI</small>;
        }

        const scrubberExpanded = isScrubberHovered || isDraggingScrubber;
        const trackHeight = scrubberExpanded ? '6px' : '4px';
        const trackTop = scrubberExpanded ? '11px' : '12px';

        return (<div
            ref={el => this.containerRef = el}
            class={`${isFullscreen ? 'video-fullscreen-container' : ''}${isCssFullscreen ? ' video-css-fullscreen' : ''}`}
            tabIndex={0}
            onKeyDown={this.handleKeyDown}
            style='outline: none;'
        >
            {videoStreamUrl ? <>
                <div class='position-relative' style={isFullscreen ? 'flex: 1 1 auto; min-height: 0; display: flex; flex-direction: column;' : ''}>
                    <video
                        preload='none'
                        ref={this.setPlayerRef}
                        playsInline
                        muted={false}
                        style={`
                            width: 100%;
                            height: 100%;
                            ${isFullscreen ? 'max-height: none; flex: 1 1 auto; min-height: 0; object-fit: contain;' : `max-height: ${this.props.maxHeight || '90vh'};`}
                            cursor: pointer;
                        `}
                        src={videoStreamUrl}
                        type={'application/x-mpegURL'}
                        poster={poster}
                        onClick={this.handleVideoClick}
                        onTouchstart={localUtil.captureEvents}
                        onTouchend={localUtil.captureEvents}
                    />

                    {/* Subtitle overlay */}
                    {currentSubtitle && (
                        <div
                            class='position-absolute w-100 text-center'
                            style='bottom: clamp(8px, 3vh, 60px); left: 0; pointer-events: none; padding: 0 clamp(0.5rem, 2vw, 1rem);'
                        >
                            <div
                                class='d-inline-block rounded'
                                style='background-color: rgba(0, 0, 0, 0.75); color: white; font-size: clamp(0.875rem, 2.5vw, 1.5rem); line-height: 1.4; max-width: 95%; padding: clamp(0.25rem, 1vh, 0.5rem) clamp(0.5rem, 2vw, 1rem); word-wrap: break-word;'
                            >
                                {currentSubtitle.text}
                            </div>
                        </div>
                    )}

                    {/* Subtitle toggle button */}
                    {hasTranscript && (
                        <button
                            type='button'
                            class={`btn btn-sm position-absolute ${subtitleLanguage ? 'btn-primary' : 'btn-secondary'} rounded-circle`}
                            style='top: 10px; right: 10px; width: 40px; height: 40px; opacity: 0.8;'
                            onClick={this.toggleTranscript}
                            title={buttonTitle}
                        >
                            {buttonContent}
                        </button>
                    )}

                    {/* Play button overlay */}
                    {!isPlaying && (
                        <div
                            class='position-absolute d-flex justify-content-center align-items-center w-100 h-100'
                            style='top: 0; left: 0; cursor: pointer; background: rgba(0,0,0,0.3);'
                            onClick={this.handleVideoClick}
                        >
                            <i class='fa-solid fa-circle-play text-white' style='font-size: 4rem; opacity: 0.8;' />
                        </div>
                    )}
                </div>

                {/* DVR control bar — outside position-relative to avoid overlapping content below */}
                {isDvrAvailable && (() => {
                    const isVod = videoStreamUrl && videoStreamUrl.includes('stream.m3u8');
                    const dvrProgress = this.getDvrProgress();
                    const displayTime = isDraggingScrubber && dragTime !== null ? dragTime : currentTime;
                    const behindSeconds = dvrEndTime - displayTime;
                    const timeLabel = isVod
                        ? `${this.formatTime(displayTime - dvrStartTime)} / ${this.formatTime(dvrEndTime - dvrStartTime)}`
                        : this.formatBehindLive(behindSeconds);
                    const scrubberColor = !isVod && isAtLiveEdge && !isDraggingScrubber ? 'bg-danger' : 'bg-primary';

                    return (
                        <div
                            class='d-flex flex-column'
                            style='background-color: rgba(0, 0, 0, 0.7);'
                        >
                            {/* Scrubber row — full width */}
                            <div
                                ref={el => this.scrubberRef = el}
                                class='position-relative mx-2'
                                style='height: 28px; cursor: pointer; touch-action: none;'
                                onClick={this.handleScrubberClick}
                                onMouseDown={this.handleScrubberDragStart}
                                onTouchStart={this.handleScrubberTouchStart}
                                onMouseMove={this.handleScrubberMouseMove}
                                onMouseLeave={this.handleScrubberMouseLeave}
                            >
                                {/* Track background */}
                                <div
                                    class='position-absolute rounded bg-secondary'
                                    style={`height: ${trackHeight}; top: ${trackTop}; left: 0; right: 0; opacity: 0.5; transition: height 0.15s ease, top 0.15s ease;`}
                                />
                                {/* Buffered ranges */}
                                {bufferedRanges.map((range, i) => (
                                    <div
                                        key={i}
                                        class='position-absolute rounded'
                                        style={`height: ${trackHeight}; top: ${trackTop}; left: ${range.start}%; width: ${range.end - range.start}%; background: rgba(255,255,255,0.3); transition: height 0.15s ease, top 0.15s ease;`}
                                    />
                                ))}
                                {/* Progress fill */}
                                <div
                                    class={`position-absolute rounded ${scrubberColor}`}
                                    style={`height: ${trackHeight}; top: ${trackTop}; left: 0; width: ${dvrProgress}%; transition: height 0.15s ease, top 0.15s ease;`}
                                />
                                {/* Scrubber handle */}
                                <div
                                    class={`position-absolute rounded-circle ${scrubberColor} ${isDraggingScrubber ? 'shadow' : ''}`}
                                    style={`
                                        width: 16px;
                                        height: 16px;
                                        top: 6px;
                                        left: calc(${dvrProgress}% - 8px);
                                        transition: ${isDraggingScrubber ? 'none' : 'left 0.1s ease-out, opacity 0.15s ease'};
                                        opacity: ${scrubberExpanded ? '1' : '0'};
                                        ${isDraggingScrubber ? 'transform: scale(1.3);' : ''}
                                    `}
                                />
                                {/* Thumbnail + time tooltip on hover */}
                                {isScrubberHovered && hoverTime !== null && !isDraggingScrubber && (() => {
                                    const { vttCues } = this.state;
                                    const thumbnail = vttCues ? this.getThumbnailForTime(hoverTime) : null;
                                    let hoverLabel;
                                    if (isVod) {
                                        hoverLabel = this.formatTime(hoverTime - dvrStartTime);
                                    } else {
                                        hoverLabel = dvrEndTime - hoverTime < 30
                                            ? 'LIVE'
                                            : this.formatBehindLive(dvrEndTime - hoverTime);
                                    }
                                    return (
                                        <div
                                            class='position-absolute d-flex flex-column align-items-center'
                                            style={`
                                                bottom: 100%;
                                                left: ${Math.max(5, Math.min(95, hoverPercent))}%;
                                                transform: translateX(-50%);
                                                margin-bottom: 8px;
                                                pointer-events: none;
                                            `}
                                        >
                                            {thumbnail && (
                                                <div style={`
                                                    width: ${thumbnail.w}px;
                                                    height: ${thumbnail.h}px;
                                                    background-image: url(${thumbnail.url});
                                                    background-position: -${thumbnail.x}px -${thumbnail.y}px;
                                                    border: 2px solid rgba(255,255,255,0.8);
                                                    border-radius: 3px;
                                                    margin-bottom: 4px;
                                                `} />
                                            )}
                                            <div style='background: rgba(0,0,0,0.85); color: white; font-size: 0.75rem; padding: 2px 6px; border-radius: 3px; white-space: nowrap;'>
                                                {hoverLabel}
                                            </div>
                                        </div>
                                    );
                                })()}
                            </div>

                            {/* Buttons row */}
                            <div class='d-flex align-items-center px-2 pb-1'>
                                {/* Play/Pause button */}
                                <button
                                    type='button'
                                    class='btn btn-sm btn-outline-light me-1 py-0'
                                    style='font-size: 0.85em; min-width: 32px; min-height: 28px;'
                                    onClick={this.togglePlayPause}
                                >
                                    <i class={`fa-solid fa-${isPlaying ? 'pause' : 'play'}`} />
                                </button>

                                {/* Start button */}
                                <button
                                    type='button'
                                    class='btn btn-sm btn-outline-light me-1 py-0'
                                    style='font-size: 0.85em; min-width: 32px; min-height: 28px;'
                                    onClick={this.seekToStart}
                                    title='Seek to start of DVR window'
                                >
                                    <i class='fa-solid fa-backward-step' />
                                </button>

                                {/* Time / LIVE button */}
                                {isVod ? (
                                    <span
                                        class='text-white py-0 px-1'
                                        style='font-size: 0.75em; min-width: 50px; min-height: 28px; line-height: 28px;'
                                    >
                                        {timeLabel}
                                    </span>
                                ) : isAtLiveEdge ? (
                                    <button
                                        type='button'
                                        class='btn btn-sm btn-danger py-0'
                                        style='font-size: 0.75em; min-width: 50px; min-height: 28px;'
                                        onClick={this.seekToLive}
                                    >
                                        <i class='fa-solid fa-circle me-1' style='font-size: 0.5em; vertical-align: middle;' />
                                        LIVE
                                    </button>
                                ) : (
                                    <button
                                        type='button'
                                        class='btn btn-sm btn-outline-secondary py-0'
                                        style='font-size: 0.75em; min-width: 50px; min-height: 28px;'
                                        onClick={this.seekToLive}
                                        title='Jump to live'
                                    >
                                        {timeLabel}
                                    </button>
                                )}

                                <div class='flex-grow-1' />

                                {/* Volume control */}
                                <div class='position-relative'>
                                    <button
                                        type='button'
                                        class='btn btn-sm btn-outline-light py-0'
                                        style='font-size: 0.85em; min-width: 32px; min-height: 28px;'
                                        onClick={this.toggleVolumeSlider}
                                        title='Volume'
                                    >
                                        <i class={`fa-solid ${isMuted || volume === 0 ? 'fa-volume-xmark' : volume < 0.5 ? 'fa-volume-low' : 'fa-volume-high'}`} />
                                    </button>
                                    {showVolumeSlider && (
                                        <div
                                            class='position-absolute rounded'
                                            style='bottom: 100%; left: 50%; transform: translateX(-50%); margin-bottom: 6px; width: 32px; height: 110px; padding: 12px 0; background-color: rgba(0, 0, 0, 0.8); display: flex; align-items: center; justify-content: center;'
                                        >
                                            <input
                                                type='range'
                                                class='form-range'
                                                style='width: 80px; transform: rotate(-90deg);'
                                                min='0'
                                                max='1'
                                                step='0.05'
                                                value={isMuted ? 0 : volume}
                                                onInput={this.handleVolumeChange}
                                            />
                                        </div>
                                    )}
                                </div>

                                {/* Fullscreen button */}
                                <button
                                    type='button'
                                    class='btn btn-sm btn-outline-light ms-1 py-0'
                                    style='font-size: 0.85em; min-width: 32px; min-height: 28px;'
                                    onClick={this.toggleFullscreen}
                                    title={isFullscreen ? 'Exit fullscreen' : 'Fullscreen'}
                                >
                                    <i class={`fa-solid ${isFullscreen ? 'fa-compress' : 'fa-expand'}`} />
                                </button>
                            </div>
                        </div>
                    );
                })()}
            </> : <>
                No video stream URL
            </>}
        </div>);
    }
}

export default VideoStreamingPlayer;
